import * as React from "react";
import * as Sentry from "@sentry/react";
import { Button, Card, Pagination } from "@jhool-io/fe-components";
import { Document, Page } from "react-pdf";
import { useFetchSingleRemitEOB } from "../../hooks/remits.queries";
import { RemitSource } from "../../types/remits.types";
import ListState from "../../../../components/ListState/ListState";
import Skeleton from "../../../../components/Skeleton/Skeleton";
import ZoomInIcon from "../../../../components/Icons/ZoomIn";
import ZoomOutIcon from "../../../../components/Icons/ZoomOut";
import { useFetchUserDetails } from "../../../../hooks/queries/user";
import { useFetchDocument } from "../../../../hooks/queries";

interface RemitEOBProps {
    remittanceId: string;
    remitSource?: RemitSource;
    batchEobUrl?: string | null;
}

export default function RemitEOB({
    remittanceId,
    remitSource,
    batchEobUrl,
}: RemitEOBProps) {
    const [EOBpageNumber, setEOBPageNumber] = React.useState(1);
    const [EOBNumPages, setEOBNumPages] = React.useState<null | number>(null);
    const [scale, setScale] = React.useState(3);
    const loggedInUser = useFetchUserDetails();

    const isManual = remitSource === RemitSource.MANUAL;

    const singleRemitEOB = useFetchSingleRemitEOB(
        remittanceId,
        Boolean(remittanceId) && !isManual
    );
    const manualRemitEOB = useFetchDocument(
        batchEobUrl ?? "",
        isManual && Boolean(batchEobUrl)
    );

    const eobQuery = isManual ? manualRemitEOB : singleRemitEOB;
    const eobBase64 = eobQuery.data?.data;
    const contentType = isManual
        ? manualRemitEOB.data?.content_type ?? "application/pdf"
        : "application/pdf";
    const isImage = contentType.startsWith("image/");
    const fileSource = React.useMemo(
        () =>
            eobBase64 ? `data:${contentType};base64,${eobBase64}` : undefined,
        [contentType, eobBase64]
    );

    const zoomIn = () => {
        if (scale < 3) {
            setScale((prevScale: number) => prevScale + 0.5);
        }
    };

    const zoomOut = () => {
        if (scale > 1) {
            setScale((prevScale: number) => prevScale - 0.5);
        }
    };

    if (isManual && !batchEobUrl) return null;

    return (
        <Card classNames="p-24 overflow-x-auto max-w-full">
            <p className="text-sm font-bold" id="eob-section">
                EOB
            </p>
            {eobQuery.isLoading && (
                <Skeleton
                    className="w-full h-[500px]"
                    containerTestId="remit-eob-loader"
                />
            )}
            {eobQuery.error && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg="Cannot display remit EOB at this time. Please try again later"
                    cardClass="mt-[--spacing-items]"
                />
            )}
            {eobBase64 && (
                <div className="p-8 bg-secondary-light rounded-r12 border border-[rgba(231,_231,_231,_0.4)] flex justify-center">
                    {isImage ? (
                        <img
                            className="max-h-[700px] max-w-full object-contain"
                            src={fileSource}
                            alt="Remit EOB"
                        />
                    ) : (
                        <Document
                            error={
                                <span className="text-danger text-sm flex items-center justify-center font-medium h-12">
                                    Could not load pdf at this time
                                </span>
                            }
                            loading={
                                <span className="text-sm flex items-center justify-center font-medium h-12">
                                    Loading pdf..
                                </span>
                            }
                            onLoadSuccess={({ numPages: nextNumPages }) => {
                                setEOBNumPages(nextNumPages);
                                setEOBPageNumber(1);
                            }}
                            onLoadError={(error) => {
                                Sentry.logger.error(
                                    "Remit EOB PDF failed to load",
                                    {
                                        remittance_id: remittanceId,
                                        user_id:
                                            loggedInUser.data?.user_id ??
                                            "unknown",
                                        error_name: error.name,
                                        error_message: error.message,
                                        payload_size: eobBase64?.length ?? 0,
                                    }
                                );
                            }}
                            onItemClick={({ pageNumber: itemPageNumber }) => {
                                setEOBPageNumber(Number(itemPageNumber));
                            }}
                            file={fileSource}
                        >
                            <Page
                                pageNumber={EOBpageNumber}
                                height={550}
                                scale={scale}
                                className="!w-full"
                                renderAnnotationLayer={false}
                            />
                            <div className="flex justify-center items-center mt-6">
                                <Button
                                    variant="normal"
                                    onClick={zoomIn}
                                    disabled={scale === 3}
                                >
                                    <ZoomInIcon />
                                </Button>
                                <Button
                                    variant="normal"
                                    onClick={zoomOut}
                                    disabled={scale === 1}
                                >
                                    <ZoomOutIcon />
                                </Button>
                            </div>
                            {EOBNumPages && EOBNumPages > 1 && (
                                <Pagination
                                    totalCount={EOBNumPages}
                                    totalPages={EOBNumPages}
                                    currentPage={EOBpageNumber}
                                    onPageChange={(page) =>
                                        setEOBPageNumber(page)
                                    }
                                    pageSize={1}
                                    isCentered
                                />
                            )}
                        </Document>
                    )}
                </div>
            )}
        </Card>
    );
}
